import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Timer, ArrowRight, RotateCcw, CheckCircle2, XCircle } from "lucide-react";
import { toast } from "sonner";
import { incrementMocksDone, hasBeenPromptedToRate, markRatePrompted, openPlayStoreRating } from "../lib/appFeedback";

export default function MockTest() {
  const [phase, setPhase] = useState("idle"); // idle | loading | active | done
  const [qs, setQs] = useState([]);
  const [answers, setAnswers] = useState({});
  const [timeLeft, setTimeLeft] = useState(0);
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (phase !== "active") return;
    if (timeLeft <= 0) {
      finish();
      return;
    }
    const t = setTimeout(() => setTimeLeft((s) => s - 1), 1000);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase, timeLeft]);

  const start = async () => {
    setPhase("loading");
    try {
      const { data } = await api.post("/mock/generate", {});
      if (!data.questions?.length) throw new Error("Empty");
      setQs(data.questions);
      setAnswers({});
      setTimeLeft(data.duration_seconds || 1200);
      setPhase("active");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start mock test");
      setPhase("idle");
    }
  };

  const finish = async () => {
    const score = qs.reduce((acc, q, i) => acc + (answers[i] === q.answer_index ? 1 : 0), 0);
    const byChapter = {};
    qs.forEach((q, i) => {
      const key = q.chapter_id || "other";
      if (!byChapter[key]) byChapter[key] = { chapter_id: key, chapter_title: q.chapter_title, correct: 0, total: 0 };
      byChapter[key].total += 1;
      if (answers[i] === q.answer_index) byChapter[key].correct += 1;
    });
    const breakdown = Object.values(byChapter);
    const duration = (qs.length ? 1200 : 0) - timeLeft;
    try {
      await api.post("/mock/submit", { score, total: qs.length, breakdown, duration_seconds: duration });
    } catch {
      /* ignore */
    }
    setResult({ score, total: qs.length, breakdown });
    setPhase("done");

    // Track mocks completed locally to trigger the rating prompt after 3 attempts.
    const mocksDone = incrementMocksDone();
    if (mocksDone >= 3 && !hasBeenPromptedToRate()) {
      markRatePrompted();
      // small delay so user first sees their score
      setTimeout(() => {
        toast(
          "You've completed 3 mock tests! Enjoying BrimiH&G? A quick Play Store rating helps a lot.",
          {
            duration: 10000,
            action: {
              label: "Rate now",
              onClick: openPlayStoreRating,
            },
          }
        );
      }, 1500);
    }
  };

  const mm = String(Math.floor(timeLeft / 60)).padStart(2, "0");
  const ss = String(timeLeft % 60).padStart(2, "0");

  if (phase === "idle")
    return (
      <div className="max-w-2xl mx-auto brimi-card p-8 text-center stagger-in">
        <div className="w-14 h-14 grid place-items-center bg-brimi-terracotta/15 text-brimi-terracotta rounded-2xl mx-auto">
          <Timer size={24} />
        </div>
        <h1 className="text-3xl sm:text-4xl font-heading font-bold text-brimi-navyText mt-4">20-Question Mock Test</h1>
        <p className="text-brimi-navyText/70 mt-2">
          Mixed History & Civics + Geography from across the ICSE 2028 syllabus. 20 minutes. Chapter-wise breakdown at the end.
        </p>
        <button onClick={start} className="brimi-btn-primary mt-6" data-testid="mock-start-btn">
          Start mock test <ArrowRight size={16} />
        </button>
      </div>
    );

  if (phase === "loading")
    return <p className="text-brimi-navyText/70 text-center py-12">Generating your mock test…</p>;

  if (phase === "done" && result)
    return (
      <div className="space-y-6">
        <div className="brimi-card p-8 text-center">
          <p className="text-xs uppercase tracking-widest font-semibold text-brimi-navyText/60">Your score</p>
          <p className="text-5xl font-heading font-bold text-brimi-navyText mt-1" data-testid="mock-score">
            {result.score}/{result.total}
          </p>
          <p className="text-brimi-navyText/70 mt-1">{Math.round((result.score / result.total) * 100)}% correct</p>
          <button onClick={start} className="brimi-btn-primary mt-5" data-testid="mock-restart-btn">
            <RotateCcw size={16} /> Take another
          </button>
        </div>
        <div className="brimi-card p-6">
          <h3 className="font-heading font-semibold text-lg text-brimi-navyText mb-4">Chapter breakdown</h3>
          <div className="space-y-3">
            {result.breakdown.map((b) => (
              <div key={b.chapter_id} className="flex items-center gap-3" data-testid={`breakdown-${b.chapter_id}`}>
                <div className="flex-1">
                  <p className="text-sm text-brimi-navyText">{b.chapter_title}</p>
                  <div className="h-1.5 mt-1 bg-brimi-creamSoft rounded-full overflow-hidden">
                    <div
                      className="h-full bg-brimi-sage"
                      style={{ width: `${b.total ? (b.correct / b.total) * 100 : 0}%` }}
                    />
                  </div>
                </div>
                <p className="text-sm font-semibold text-brimi-navyText">
                  {b.correct}/{b.total}
                </p>
              </div>
            ))}
          </div>
        </div>

        <div className="brimi-card p-6">
          <h3 className="font-heading font-semibold text-lg text-brimi-navyText mb-4">
            Review answers
          </h3>
          <p className="text-sm text-brimi-navyText/65 mb-5">
            The correct answer is highlighted in <span className="text-brimi-sage font-semibold">green</span>.
            Any wrong pick you made is shown in <span className="text-brimi-terracotta font-semibold">red</span>.
          </p>
          <div className="space-y-5">
            {qs.map((q, i) => {
              const userPick = answers[i];
              const correctIdx = q.answer_index;
              const isUnanswered = userPick === undefined;
              const isCorrect = !isUnanswered && userPick === correctIdx;
              return (
                <div
                  key={i}
                  className="border border-brimi-creamSoft rounded-2xl p-4 sm:p-5"
                  data-testid={`review-q-${i}`}
                >
                  <div className="flex items-start gap-3">
                    <div
                      className={`w-7 h-7 grid place-items-center rounded-full flex-shrink-0 ${
                        isCorrect
                          ? "bg-brimi-sage/20 text-brimi-sage"
                          : "bg-brimi-terracotta/15 text-brimi-terracotta"
                      }`}
                    >
                      {isCorrect ? <CheckCircle2 size={16} /> : <XCircle size={16} />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs uppercase tracking-widest font-semibold text-brimi-navyText/50">
                        {q.chapter_title}
                      </p>
                      <p className="font-medium text-brimi-navyText mt-1">
                        <span className="text-brimi-navyText/50">{i + 1}.</span> {q.q}
                      </p>
                    </div>
                  </div>
                  <div className="mt-3 grid sm:grid-cols-2 gap-2">
                    {q.options.map((opt, oi) => {
                      const isRightOpt = oi === correctIdx;
                      const isUserOpt = oi === userPick;
                      let cls =
                        "text-left px-4 py-2.5 rounded-xl border text-sm flex items-start gap-2";
                      if (isRightOpt) {
                        cls +=
                          " bg-brimi-sage/10 border-brimi-sage/40 text-brimi-navyText font-medium";
                      } else if (isUserOpt) {
                        cls +=
                          " bg-brimi-terracotta/10 border-brimi-terracotta/40 text-brimi-navyText";
                      } else {
                        cls += " border-brimi-creamSoft text-brimi-navyText/75";
                      }
                      return (
                        <div
                          key={oi}
                          className={cls}
                          data-testid={`review-q-${i}-opt-${oi}`}
                        >
                          <span className="font-mono text-xs text-brimi-navyText/50 mt-0.5">
                            {String.fromCharCode(65 + oi)}
                          </span>
                          <span className="flex-1">{opt}</span>
                          {isRightOpt && (
                            <span className="text-xs font-semibold text-brimi-sage whitespace-nowrap">
                              ✓ Correct
                            </span>
                          )}
                          {isUserOpt && !isRightOpt && (
                            <span className="text-xs font-semibold text-brimi-terracotta whitespace-nowrap">
                              Your pick
                            </span>
                          )}
                        </div>
                      );
                    })}
                  </div>
                  {isUnanswered && (
                    <p className="text-xs text-brimi-navyText/55 mt-3 italic">
                      You didn&apos;t answer this question.
                    </p>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );

  // active phase
  return (
    <div className="space-y-6">
      <div className="sticky top-16 z-20 bg-brimi-cream/95 backdrop-blur -mx-5 px-3 sm:px-5 py-2.5 sm:py-3 border-b border-brimi-creamSoft flex items-center justify-between gap-2">
        <span className="text-xs sm:text-sm text-brimi-navyText/70 whitespace-nowrap" data-testid="mock-progress">
          <span className="font-semibold text-brimi-navyText">{Object.keys(answers).length}</span>
          <span className="text-brimi-navyText/50">/{qs.length}</span>
          <span className="hidden sm:inline"> answered</span>
        </span>
        <div className="brimi-pill bg-brimi-terracotta/15 text-brimi-terracotta whitespace-nowrap !px-2.5 !py-1 text-xs sm:text-xs" data-testid="mock-timer">
          ⏱ {mm}:{ss}
        </div>
        <button
          onClick={finish}
          className="brimi-btn-primary !py-2 !px-3 sm:!px-5 text-sm whitespace-nowrap"
          data-testid="mock-finish-btn"
        >
          Finish
        </button>
      </div>
      <div className="space-y-5">
        {qs.map((q, i) => (
          <div key={i} className="brimi-card p-5" data-testid={`mock-q-${i}`}>
            <p className="text-xs uppercase tracking-widest font-semibold text-brimi-navyText/50">{q.chapter_title}</p>
            <p className="font-medium text-brimi-navyText mt-1">
              <span className="text-brimi-navyText/50">{i + 1}.</span> {q.q}
            </p>
            <div className="mt-3 grid sm:grid-cols-2 gap-2">
              {q.options.map((opt, oi) => {
                const isPicked = answers[i] === oi;
                return (
                  <button
                    key={oi}
                    onClick={() => setAnswers((a) => ({ ...a, [i]: oi }))}
                    className={`text-left px-4 py-2.5 rounded-xl border transition ${
                      isPicked
                        ? "bg-brimi-navy/10 border-brimi-navy/30 text-brimi-navyText"
                        : "border-brimi-creamSoft hover:border-brimi-navy/30 text-brimi-navyText/85"
                    }`}
                    data-testid={`mock-q-${i}-opt-${oi}`}
                  >
                    <span className="font-mono text-xs text-brimi-navyText/50 mr-2">{String.fromCharCode(65 + oi)}</span>
                    {opt}
                  </button>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
